import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useParams, Link, useNavigate } from 'react-router-dom';
import ProductService from '../services/product.service';
import { Search, X, SlidersHorizontal } from 'lucide-react';
import toast from 'react-hot-toast';
import { debounce } from 'lodash';
import { formatPrice } from '../utils/currency';

const ProductListingPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { slug } = useParams();

    // Get initial query from URL
    const initialQuery = searchParams.get('q') || '';
    const [searchTerm, setSearchTerm] = useState(initialQuery);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch Products Function
    const fetchProducts = useCallback(async (queryParam) => {
        setLoading(true);
        try {
            const params = {};
            // Use the passed queryParam to be sure we are using the latest, 
            // or fallback to what is in the URL if not provided explicitly?
            // Safer to trust the arguments or the current URL state at the moment of effect.
            // Let's rely on the arguments passed or current scope if called directly.

            // Actually, best practice: depend on the arguments.
            const currentQ = queryParam !== undefined ? queryParam : searchParams.get('q');

            if (currentQ) params.search = currentQ;
            if (slug) params.category_slug = slug;

            // Corrected method name: getProducts (was getAllProducts)
            const response = await ProductService.getProducts(params);
            setProducts(response.data.results || response.data || []);
        } catch (error) {
            console.error(error);
            // toast.error("Failed to load products");
        } finally {
            setLoading(false);
        }
    }, [slug, searchParams]);
    // Note: Depends on searchParams. If searchParams changes, this function is recreated.
    // But we want to call it when searchParams changes.

    // Debounced URL updater
    const debouncedSetUrl = useCallback(
        debounce((query) => {
            if (query) {
                setSearchParams({ q: query });
            } else {
                setSearchParams({});
            }
        }, 500),
        [setSearchParams]
    );

    // Initial Load & URL Change Effect
    useEffect(() => {
        // When URL params change (q or slug), fetch data
        const currentQ = searchParams.get('q');
        fetchProducts(currentQ);
    }, [searchParams, slug, fetchProducts]);

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            debouncedSetUrl.cancel();
        };
    }, [debouncedSetUrl]);

    // Handle Input Change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedSetUrl(value);
    };

    // Handle Manual Submit (Enter or Icon Click)
    const handleSearchSubmit = () => {
        debouncedSetUrl.cancel();
        if (searchTerm) {
            setSearchParams({ q: searchTerm });
        } else {
            setSearchParams({});
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearchSubmit();
        }
    };

    const clearSearch = () => {
        setSearchTerm('');
        setSearchParams({});
    };

    // Update local input state if URL changes externally (e.g. back button)
    useEffect(() => {
        const urlQuery = searchParams.get('q') || '';
        if (urlQuery !== searchTerm) {
            setSearchTerm(urlQuery);
        }
    }, [searchParams]); // This might cause a double render but ensures input sync

    return (
        <div className="max-w-7xl mx-auto py-8 md:py-12 px-6 min-h-screen">

            {/* Search Header */}
            <div className="mb-12 max-w-3xl mx-auto text-center">
                <h1 className="font-serif text-3xl md:text-4xl capitalize mb-8">
                    {slug ? slug.replace(/-/g, ' ') : 'Search'}
                </h1>

                {!slug && (
                    <div className="relative group">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            onKeyDown={handleKeyDown}
                            placeholder="What are you looking for?"
                            className="w-full px-6 py-4 pl-14 pr-12 rounded-full border border-gray-200 outline-none focus:border-black focus:ring-1 focus:ring-black transition-all text-lg shadow-sm"
                            autoFocus
                        />
                        <button
                            onClick={handleSearchSubmit}
                            className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-black transition-colors hover:text-black"
                        >
                            <Search size={24} />
                        </button>

                        {searchTerm && (
                            <button
                                onClick={clearSearch}
                                className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black p-1"
                            >
                                <X size={20} />
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Results Header */}
            <div className="flex items-center justify-between mb-8 border-b border-gray-100 pb-4">
                <div className="text-sm text-gray-500 uppercase tracking-widest">
                    {loading ? 'Searching...' : `${products.length} Result${products.length !== 1 ? 's' : ''}`}
                </div>
                <button className="flex items-center gap-2 text-xs uppercase tracking-widest hover:opacity-60">
                    <SlidersHorizontal size={14} /> Filter
                </button>
            </div>

            {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-12">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                        <div key={i} className="animate-pulse">
                            <div className="aspect-[3/4] bg-gray-100 mb-4 rounded-sm" />
                            <div className="h-4 bg-gray-100 w-3/4 mb-2" />
                            <div className="h-4 bg-gray-100 w-1/4" />
                        </div>
                    ))}
                </div>
            ) : products.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
                    {products.map((product) => (
                        <Link to={`/product/${product.id}`} key={product.id} className="group block cursor-pointer">
                            <div className="aspect-[3/4] bg-gray-100 mb-4 overflow-hidden relative group rounded-sm">
                                {product.primary_image || (product.images && product.images[0]) ? (
                                    <img
                                        src={product.primary_image || (product.images && product.images[0]?.image_url)}
                                        alt={product.name}
                                        className="w-full h-full object-cover transition-transform duration-700 ease-in-out group-hover:scale-105"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-300 uppercase text-xs tracking-widest">No Image</div>
                                )}

                                {product.is_new && (
                                    <span className="absolute top-3 left-3 bg-white px-2 py-1 text-[10px] uppercase tracking-widest font-bold">New</span>
                                )}
                            </div>

                            <div className="space-y-1">
                                <p className="text-[10px] text-gray-400 uppercase tracking-widest">{product.brand_name || product.brand?.name || 'Seeker'}</p>
                                <h3 className="font-serif text-lg leading-tight group-hover:underline decoration-1 underline-offset-4">{product.name}</h3>
                                <p className="text-sm font-medium font-sans">{formatPrice(product.base_price)}</p>
                            </div>
                        </Link>
                    ))}
                </div>
            ) : (
                <div className="py-24 text-center">
                    <p className="font-serif text-2xl text-gray-400 mb-4">
                        {searchTerm ? `No results for "${searchTerm}"` : 'No products found'}
                    </p>
                    <div className="flex justify-center gap-4">
                        <button onClick={clearSearch} className="text-xs uppercase tracking-widest border-b border-black pb-1 hover:opacity-60">
                            View All Products
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductListingPage;
